import { Link } from 'react-router-dom'
import PolicyLayout from '../components/PolicyLayout.jsx'

const toc = [
  { id: 'audience', label: 'Who the app is for' },
  { id: 'collection', label: 'Information we collect' },
  { id: 'use', label: 'How we use information' },
  { id: 'disclosure', label: 'When information is disclosed' },
  { id: 'retention', label: 'Retention and deletion' },
  { id: 'security', label: 'Security' },
  { id: 'choices', label: 'Your choices' },
  { id: 'changes', label: 'Policy changes' },
  { id: 'contact', label: 'Contact' },
]

export default function AcademicEdgePolicyPage() {
  return (
    <PolicyLayout
      effectiveDate="July 31, 2026"
      footerNote="Academic Edge is intended for approved users age 13 and older."
      label="iOS application · 13+"
      title="Academic Edge Privacy Policy"
      toc={toc}
    >
      <p className="policy-intro">
        Academic Edge is a planning and organization application operated by Zpace Studio
        (&quot;Zpace Studio,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). This
        Privacy Policy explains how information is collected, used, disclosed, retained, and
        protected when you use Academic Edge.
      </p>

      <section id="audience">
        <h2>Who Academic Edge is for</h2>
        <p>
          Academic Edge is intended for students age 13 and older, including secondary school
          and college students. It is not intended for children under 13, and we do not
          knowingly collect personal information from children under 13.
        </p>
        <p>
          Academic Edge does not currently offer public, self-service account creation inside
          the app. An account and the appropriate membership must be created or approved by the
          operator before a person can use the app&apos;s planning features. Public availability
          of an app download or information page does not grant access to Academic Edge.
        </p>
        <div className="notice-box">
          <p>
            If we learn that information from a child under 13 has been collected, we will
            disable the account and take reasonable steps to delete the information. A parent or
            guardian who believes a child under 13 has provided information may contact us at{' '}
            <a href="mailto:info@edgezpace.com">info@edgezpace.com</a>.
          </p>
        </div>
      </section>

      <section id="collection">
        <h2>Information we collect</h2>
        <h3>Account and access information</h3>
        <p>We may process:</p>
        <ul>
          <li>your email address, display name, account identifier, and authentication information;</li>
          <li>the spaces, memberships, and access roles connected to your account; and</li>
          <li>account status, invitation, sign-in, and password-recovery information.</li>
        </ul>
        <p>
          Passwords are handled by our authentication provider. We do not have access to your
          plain-text password.
        </p>

        <h3>Planning information and content</h3>
        <p>
          Academic Edge stores information you or an authorized member of your shared space
          enters into the app. Depending on how the app is used, this may include:
        </p>
        <ul>
          <li>
            projects, tasks, project steps, checklists, titles, descriptions, notes, and linked
            resources;
          </li>
          <li>
            assignments, status, priorities, importance and effort preferences, estimated time,
            due dates, start dates, and recurring-work settings;
          </li>
          <li>
            availability preferences, protected-time blocks, schedules, and notification
            preferences;
          </li>
          <li>completion and activity history; and</li>
          <li>
            files, images, links, file names, file types, and other attachments you choose to
            add.
          </li>
        </ul>
        <p>
          Some information may be visible to other authorized members of the same planner space,
          depending on their assigned role. Do not add sensitive information that is not
          necessary for planning or collaboration.
        </p>

        <h3>Calendar, reminders, and notifications</h3>
        <p>
          If you choose to grant permission, Academic Edge may access calendar events or device
          reminders so the app can show scheduling constraints and planning context. This
          information may include event or reminder titles, dates and times, recurrence details,
          notes, completion status, and locations supplied in those records.
        </p>
        <p>
          The app also may request notification permission to provide reminders you enable. You
          can revoke calendar, reminder, or notification permission in your device settings.
          Features that depend on a revoked permission may stop working.
        </p>

        <h3>AI-assisted planning</h3>
        <p>
          When an AI-assisted feature is used, Academic Edge may send the planning context needed
          for the request to OpenAI. Depending on the feature, that context may contain project
          or task titles and descriptions, checklist items, dates, status, priority and effort
          information, availability, or recent activity. OpenAI returns a planning suggestion,
          such as a recommended focus, next action, step list, or daily reset.
        </p>
        <p>
          AI suggestions may be inaccurate and should be reviewed before use. Avoid placing
          confidential, highly sensitive, or legally protected information in content submitted
          to an AI-assisted feature.
        </p>

        <h3>Technical information</h3>
        <p>
          Our hosting, authentication, and application-service providers may process technical
          information needed to deliver and secure the service. This can include IP address,
          device or browser type, operating-system and app version, request timestamps,
          authentication events, network diagnostics, and error or security logs.
        </p>
      </section>

      <section id="use">
        <h2>How we use information</h2>
        <p>We use information to:</p>
        <ul>
          <li>authenticate users and enforce account, membership, and role permissions;</li>
          <li>provide, synchronize, and maintain planning and collaboration features;</li>
          <li>store files and user-selected settings;</li>
          <li>provide calendar, reminder, notification, and AI-assisted features when requested;</li>
          <li>respond to support, privacy, correction, and deletion requests;</li>
          <li>diagnose problems, prevent abuse, and protect accounts and the service; and</li>
          <li>comply with applicable legal obligations.</li>
        </ul>
        <p>
          <strong>
            We do not sell personal information. Academic Edge does not use personal information
            for third-party advertising.
          </strong>
        </p>
      </section>

      <section id="disclosure">
        <h2>When information is disclosed</h2>
        <p>We disclose information only as needed for the purposes described in this policy, including to:</p>
        <ul>
          <li>
            <strong>Supabase</strong>, which provides authentication, database, server-function,
            and private file-storage infrastructure;
          </li>
          <li>
            <strong>OpenAI</strong>, when an AI-assisted planning feature is used, to process the
            relevant planning context and generate a response;
          </li>
          <li>
            <strong>authorized members of a shared planner space</strong>, according to their
            roles and the collaboration features being used;
          </li>
          <li>service providers that help us operate, secure, diagnose, or support the application; and</li>
          <li>
            government authorities or other parties when reasonably necessary to comply with
            law, protect rights or safety, investigate abuse, or enforce applicable terms.
          </li>
        </ul>
        <p>
          These providers may process information in locations different from your own and are
          governed by their own legal terms and privacy commitments.
        </p>
      </section>

      <section id="retention">
        <h2>Data retention and deletion</h2>
        <p>
          We retain account and planning information while an account is active and for as long
          as reasonably necessary to provide the service, maintain security, resolve disputes,
          meet legal obligations, and complete approved deletion requests. Security logs and
          backup copies may remain for a limited period after information is removed from active
          systems.
        </p>
        <p>
          Academic Edge does not yet provide a self-service deletion control. To request access,
          correction, export, or deletion of your account and associated data, email{' '}
          <a href="mailto:info@edgezpace.com">info@edgezpace.com</a> from the address connected to
          the account. We may ask for information reasonably necessary to verify the request and
          protect the account from unauthorized deletion.
        </p>
        <p>
          We will delete or de-identify information associated with a verified request unless
          retention is required by law or is reasonably necessary for security, fraud
          prevention, or the integrity of shared records. If we must retain particular
          information, we will explain the reason when reasonably possible.
        </p>
        <p>
          You may also delete individual planning items or attachments through available app
          controls. Deleting content shared with a planner space may affect other authorized
          members of that space.
        </p>
      </section>

      <section id="security">
        <h2>Security</h2>
        <p>
          We use administrative and technical safeguards designed to protect information. These
          include authenticated access, role- and membership-based database rules, encrypted
          network connections, and private storage controls for uploaded files. No system can
          guarantee absolute security, and users should protect their passwords and devices.
        </p>
      </section>

      <section id="choices">
        <h2>Your choices</h2>
        <p>You can:</p>
        <ul>
          <li>choose whether to grant calendar, reminder, and notification permissions;</li>
          <li>limit the content and attachments you enter;</li>
          <li>review or remove content using available app controls;</li>
          <li>sign out of the app; and</li>
          <li>contact us to request access, correction, export, or deletion.</li>
        </ul>
      </section>

      <section id="changes">
        <h2>Changes to this policy</h2>
        <p>
          We may update this Privacy Policy when Academic Edge, its providers, or legal
          requirements change. The revised policy will show a new effective date. If a change
          materially affects how existing personal information is used, we will provide
          additional notice when appropriate.
        </p>
      </section>

      <section id="contact">
        <h2>Contact</h2>
        <p>For privacy questions or requests, contact:</p>
        <p>
          <strong>Zpace Studio</strong>
          <br />
          <a href="mailto:info@edgezpace.com">info@edgezpace.com</a>
        </p>
        <div className="back-row">
          <Link className="text-link" to="/privacy/">
            Return to the privacy library
          </Link>
        </div>
      </section>
    </PolicyLayout>
  )
}
